import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AgencyBusinessAssociateCompanies.graphql';
import { Query, QueryagencyBusinessAssociateCompaniesArgs } from '../../__generated__/globalTypes';

export const useAgencyBusinessAssociateCompaniesQuery = (
  options?: QueryHookOptions<Query, QueryagencyBusinessAssociateCompaniesArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryagencyBusinessAssociateCompaniesArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAgencyBusinessAssociateCompaniesLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryagencyBusinessAssociateCompaniesArgs>
): QueryResult<Query, QueryagencyBusinessAssociateCompaniesArgs> & {
  getAgencyBusinessAssociateCompanies: LazyQueryExecFunction<Query, QueryagencyBusinessAssociateCompaniesArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAgencyBusinessAssociateCompanies, result] = useLazyQuery<Query, QueryagencyBusinessAssociateCompaniesArgs>(
    QUERY,
    {
      onError: (error) => {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      },
      ...options
    }
  );

  return { getAgencyBusinessAssociateCompanies, ...result };
};

export const useAgencyBusinessAssociateCompaniesPromiseQuery = () => {
  const client = useApolloClient();

  const getAgencyBusinessAssociateCompanies = useCallback(
    (options?: Omit<QueryOptions<QueryagencyBusinessAssociateCompaniesArgs, Query>, 'query'>) =>
      client.query<Query, QueryagencyBusinessAssociateCompaniesArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAgencyBusinessAssociateCompanies };
};
// Suspense Query Hooks
export const useAgencyBusinessAssociateCompaniesSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryagencyBusinessAssociateCompaniesArgs>
) => {
  const result = useSuspenseQuery<Query, QueryagencyBusinessAssociateCompaniesArgs>(QUERY, {
    ...options
  });

  return result;
};
